/**
 * @file config.ts
 * @description GCOME_AP 磁轴键盘板载配置读取实现。
 *
 * 板载控制区（GetBase, CMD=0x04）32 字节：
 *   偏移 0     — 当前配置分组（0x00-0x03）
 *   偏移 1     — 最大分组数量（0x04）
 *   偏移 2-5   — 当前分组顺序（4 字节，每字节一个索引）
 *
 * 配置名称存储在 APP 自行管理的数据区（GetAppDefine/SetAppDefine）：
 *   通过 0xf1/0xf2 读写，数据格式为 JSON 字符串数组。
 *   地址 0x0280 开始，需分帧读取：每帧 56 字节，地址递增。
 *   设备初始化（恢复出厂）时该区被清空，SDK 独占，无需避让外部工具数据。
 */

import { ConfigFeature } from '../../../../features/config/contract'
import type { GcomeApKeyboardDevice } from '../../../../devices/keyboard/gcome-ap/device'
import { GCOME_AP_CMD } from '../../protocols/cmd'
import type { Config, DeviceDataSnapshot, platform } from '../../../../types/config'
import type { ConfigCapability } from '../../../../types/manifest'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { encodeStringToBytes } from '../../../../utils/StringUtils'
import type {
  LayerKeysMap,
  Macro,
  DksSlotInfo,
  MtSlotInfo,
  TglSlotInfo,
} from '../../../../types/keyboardKeys'
import type { KeyboardLightingEffect, keyMapLighting } from '../../../../types/keyboardLighting'
import { LightingDomainType } from '../../../../types/lightingDomain'
import { LIGHTING_TARGET } from '../../../../types/lighting'
import type { keysColor } from '../../../../types/color'
import type { KeyTravelList } from '../../../../types/keyTravel'
import type { GcomeApPerfStatus } from '../../../../types/keyboardPerfCfgGcomeAp'
import { formatFirmwareVersions } from '../../../../utils/firmwareVersion'
import type { GcomeApConfigPayload } from '../../types/GcomeApConfigPayload'
import type { GcomeApKeyboardKeys } from './keyboard-keys'
import {
  TRAVEL_KEY_COUNT,
  encodeTravelInfo,
  parseTravelInfo,
  readTravelBlock,
  writeTravelBlock,
  type InfoBlockProgress,
} from '../../utils/advanced-keys'
import { KeyTravelFamily, type KeyTravelWriteList } from '../../../../types/keyTravel'

/** 将分帧 done/total 映射到 [from, to] 百分比区间。 */
function mapFrameProgress(
  updateProgress: (progress: number) => void,
  from: number,
  to: number,
): InfoBlockProgress {
  const range = to - from
  return (done, total) => {
    const safeTotal = Math.max(total, 1)
    updateProgress(from + Math.round((range * done) / safeTotal))
  }
}

/** APP 自定义数据区配置名称起始地址（0x0280，名字表之后）。偏移分配参见 docs/app-define-region.md。 */
const APP_CONFIG_NAME_ADDRESS = 0x0280

/** APP 数据区总大小。 */
const APP_DEFINE_TOTAL_SIZE = 1024

/** 单帧最大数据长度。 */
const FRAME_LEN = 56

export class GcomeApKeyboardConfig extends ConfigFeature {
  /**
   * 通过 GetBase 命令读取板载配置信息（单帧，LEN=56）。
   */
  private async readBase(): Promise<Uint8Array> {
    const device = this.device as unknown as GcomeApKeyboardDevice
    const res = await device.sendRawApCommand({
      command: GCOME_AP_CMD.getBase,
      data: new Array(FRAME_LEN).fill(0),
    })
    return res.response.data
  }

  /**
   * 分帧读取 APP 自行管理的数据区，返回完整字节数组。
   *
   * 数据从 APP_CONFIG_NAME_ADDRESS (0x0280) 起始，每帧读 56 字节，
   * 地址递增 56，直到返回的数据全部为 0xFF/0x00 空填充。
   */
  private async readAppDefine(): Promise<Uint8Array> {
    const device = this.device as unknown as GcomeApKeyboardDevice
    const chunks: Uint8Array[] = []
    const maxFrames = Math.ceil(APP_DEFINE_TOTAL_SIZE / FRAME_LEN)

    for (let i = 0; i < maxFrames; i++) {
      const address = APP_CONFIG_NAME_ADDRESS + i * FRAME_LEN
      const res = await device.sendRawApCommand({
        command: GCOME_AP_CMD.getAppDefine,
        address,
        data: new Array(FRAME_LEN).fill(0),
      })
      const chunk = res.response.data
      chunks.push(chunk)

      // 检查是否全是空填充（0xFF 为 NOR Flash 擦除态，0x00 为空）—— 说明已到数据末尾
      if (chunk.every((b: number) => b === 0xff || b === 0x00)) break

      // 返回不足 FRAME_LEN 也停止
      if (chunk.length < FRAME_LEN) break
    }

    // 合并所有块
    const totalLen = chunks.reduce((sum, c) => sum + c.length, 0)
    const result = new Uint8Array(totalLen)
    let offset = 0
    for (const chunk of chunks) {
      result.set(chunk, offset)
      offset += chunk.length
    }
    return result
  }

  /**
   * 分帧写入 APP 自行管理的数据区。
   * 每帧最多 FRAME_LEN (56) 字节，地址递增。
   * @param data         待写入的完整数据
   * @param startAddress 起始地址（默认配置名称区；清空全区时传 0）
   */
  private async writeAppDefine(
    data: Uint8Array,
    startAddress = APP_CONFIG_NAME_ADDRESS,
  ): Promise<void> {
    const device = this.device as unknown as GcomeApKeyboardDevice
    const totalFrames = Math.ceil(data.length / FRAME_LEN)

    for (let i = 0; i < totalFrames; i++) {
      const address = startAddress + i * FRAME_LEN
      const start = i * FRAME_LEN
      const end = Math.min(start + FRAME_LEN, data.length)
      const frameData = data.slice(start, end)

      // 如果整帧都是 0x00 填充，跳过不写
      if (frameData.every((b: number) => b === 0x00)) continue

      await device.sendRawApCommand({
        command: GCOME_AP_CMD.setAppDefine,
        address,
        data: Array.from(frameData),
      })
    }
  }

  /**
   * 将设备返回的原始配置名称转为标准化前端 i18n key。
   *
   * GCOME_AP 设备默认配置名称格式为 `i18n<key>N`：
   *   例：i18n<defaultOnboard>2  →  home.config.card.defaultOnboard2
   *       i18n<defaultOnboard>   →  home.config.card.defaultOnboard
   *       666666                 →  666666（用户自定义名，原样返回）
   */
  private resolveConfigName(raw: string): string {
    const match = raw.match(/^i18n<([^>]+)>(\d*)$/)
    if (match) {
      const key = match[1]
      const suffix = match[2]
      return `home.config.card.${key}${suffix}`
    }
    return raw
  }

  /**
   * 从完整的 data 字节数组中解析第一段标准 JSON 数组。
   *
   * 通过括号平衡找到第一个完整的 `[...]`，忽略其后的残留数据。
   * 防止第三方工具直接修改 Flash 后残留旧 JSON 片段导致的解析失败。
   *
   * @param resolve 是否对每个名称调用 resolveConfigName 转为 i18n key。
   *                读取（getAllConfigs/getCurrentConfigName）传 true，
   *                写回（renameConfig）传 false 以保持原始设备数据。
   */
  private parseConfigNames(data: Uint8Array, resolve = true): string[] {
    // 定位到第一个 `[` (0x5b)
    let pos = 0
    while (pos < data.length && data[pos] !== 0x5b) pos++
    if (pos >= data.length) return []

    // 括号平衡找到配对的 `]` (0x5d)
    let depth = 0
    let end = -1
    for (let i = pos; i < data.length; i++) {
      if (data[i] === 0x5b) depth++
      else if (data[i] === 0x5d) depth--
      if (depth === 0) {
        end = i + 1
        break
      }
    }
    if (end === -1) return []

    const jsonBytes = data.slice(pos, end)
    const str = new TextDecoder().decode(jsonBytes)
    try {
      const names = JSON.parse(str)
      if (Array.isArray(names)) {
        return names.map((n) => (resolve ? this.resolveConfigName(String(n)) : String(n)))
      }
    } catch {
      // 解析失败
    }
    return []
  }

  async getCurrentConfigIndex(): Promise<number> {
    const data = await this.readBase()
    return data[0]
  }

  async getCurrentConfigName(index?: number): Promise<string> {
    const realIndex = index !== undefined ? index : await this.getCurrentConfigIndex()
    const names = this.parseConfigNames(await this.readAppDefine())
    return names[realIndex] ?? ''
  }

  async getCurrentConfig(): Promise<Config> {
    const index = await this.getCurrentConfigIndex()
    const name = await this.getCurrentConfigName(index)
    this.device.currentConfig = { index, name }
    return this.device.currentConfig
  }

  async getAllConfigs(): Promise<Config[]> {
    const data = await this.readBase()
    const maxProfiles = data[1]
    const profileOrder = Array.from(data.slice(2, 6))
    const names = this.parseConfigNames(await this.readAppDefine())

    const configs: Config[] = []
    for (let i = 0; i < maxProfiles; i++) {
      const slotIndex = profileOrder[i]
      configs.push({
        index: slotIndex,
        name: names[slotIndex] ?? '',
      })
    }
    this.device.configList = configs
    return configs
  }

  async setCurrentConfig(index: number): Promise<void> {
    const device = this.device as unknown as GcomeApKeyboardDevice
    const base = await this.readBase()
    const payload = [index, base[1], base[2], base[3], base[4], base[5], 0xaa, 0xbb]
    await device.sendRawApCommand({
      command: GCOME_AP_CMD.switchOnboardProfile,
      data: payload,
    })
    const name = await this.getCurrentConfigName(index)
    this.device.currentConfig = { index, name }
  }

  /**
   * 从 manifest 中读取 config 能力字段。
   */
  private get configCap(): ConfigCapability | undefined {
    return this.device.manifest.capabilities.find((item) => item.type === 'config') as
      | ConfigCapability
      | undefined
  }

  /**
   * 从 manifest 读取配置名称最小长度，默认为 1。
   */
  private get minNameLen(): number {
    return this.configCap?.config?.minNameLen ?? 1
  }

  /**
   * 从 manifest 读取配置名称最大长度，默认为 16。
   */
  private get maxNameLen(): number {
    return this.configCap?.config?.maxNameLen ?? 16
  }

  /**
   * 从 manifest 读取最大配置数量，默认为 4。
   * 如 Ace-68-Turbo 中 maxConfig: 3，表示最多 3 个板载配置。
   */
  private get maxConfigCount(): number {
    return this.configCap?.config?.maxConfig ?? 4
  }

  async renameConfig(config: Config): Promise<void> {
    const configName = config.name.trim()
    if (configName.length === 0) {
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, '配置名称不能为空')
    }
    if (configName.length < this.minNameLen) {
      throw new DeviceError(
        DeviceErrorCode.WRITE_FAILED,
        `配置名称不能少于${this.minNameLen}个字符`,
      )
    }
    if (configName.length > this.maxNameLen) {
      throw new DeviceError(DeviceErrorCode.MAX_LIMIT_REACHED, '配置名称过长')
    }

    const names = this.parseConfigNames(await this.readAppDefine(), false)
    names[config.index] = configName

    const jsonStr = JSON.stringify(names)
    // 按帧大小对齐，避免发送多余填充数据
    const payloadLen = Math.ceil(jsonStr.length / FRAME_LEN) * FRAME_LEN
    const payload = encodeStringToBytes(jsonStr, payloadLen)

    await this.writeAppDefine(payload)
    this.device.cache.invalidateByTag(`cfg:${config.index}`)
  }

  async createConfig(name: string, index?: number): Promise<number> {
    const configName = name.trim()
    if (configName.length < this.minNameLen) {
      throw new DeviceError(
        DeviceErrorCode.WRITE_FAILED,
        `配置名称不能少于${this.minNameLen}个字符`,
      )
    }
    if (configName.length > this.maxNameLen) {
      throw new DeviceError(DeviceErrorCode.MAX_LIMIT_REACHED, '配置名称过长')
    }

    const device = this.device as unknown as GcomeApKeyboardDevice
    const base = await this.readBase()
    const maxProfiles = base[1]
    const profileOrder = Array.from(base.slice(2, 6))

    // 如果指定了 index 且已被占用，直接报错
    const usedSet = new Set(profileOrder.slice(0, maxProfiles))
    if (index !== undefined && usedSet.has(index)) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `板载配置 ${index} 已被占用`)
    }

    // 未指定 index 时自动查找空闲槽位（以 manifest 中 maxConfig 为准）
    let slotIndex = index
    if (slotIndex === undefined || slotIndex < 0) {
      for (let i = 0; i < this.maxConfigCount; i++) {
        if (!usedSet.has(i)) {
          slotIndex = i
          break
        }
      }
    }
    if (slotIndex === undefined || slotIndex < 0) {
      throw new DeviceError(DeviceErrorCode.MAX_LIMIT_REACHED, '无空闲板载槽位')
    }

    // 1. 更新板载配置顺序（0x0E）：将新槽位加入配置列表尾部，配置数 +1
    const newMaxProfiles = Math.min(maxProfiles + 1, this.maxConfigCount)
    const newProfileOrder = [...profileOrder]
    if (!usedSet.has(slotIndex)) {
      // 将新槽位放到活跃列表末尾
      newProfileOrder[maxProfiles] = slotIndex
    }
    const switchPayload = [
      slotIndex,
      newMaxProfiles,
      newProfileOrder[0],
      newProfileOrder[1],
      newProfileOrder[2],
      newProfileOrder[3],
      0xaa,
      0xbb,
    ]
    await device.sendRawApCommand({
      command: GCOME_AP_CMD.switchOnboardProfile,
      data: switchPayload,
    })

    // 2. 写入配置名称（0xF2）：先读当前所有名称，追加新名称
    const names = this.parseConfigNames(await this.readAppDefine(), false)
    while (names.length <= slotIndex) names.push('')
    names[slotIndex] = configName
    const jsonStr = JSON.stringify(names)
    const payloadLen = Math.ceil(jsonStr.length / FRAME_LEN) * FRAME_LEN
    const payload = encodeStringToBytes(jsonStr, payloadLen)
    await this.writeAppDefine(payload)

    // 3. 复位指定板载（0xEE）：初始化该槽位硬件数据
    await device.sendRawApCommand({
      command: GCOME_AP_CMD.factoryReset,
      data: [slotIndex],
    })

    this.device.cache.invalidateByTag(`cfg:${slotIndex}`)
    return slotIndex
  }

  async deleteConfig(index: number): Promise<void> {
    const device = this.device as unknown as GcomeApKeyboardDevice
    const base = await this.readBase()
    const maxProfiles = base[1]
    const profileOrder = Array.from(base.slice(2, 6))

    // 1. 从配置名称列表中清除
    const names = this.parseConfigNames(await this.readAppDefine(), false)
    if (index < names.length) {
      names[index] = ''
    }
    const jsonStr = JSON.stringify(names)
    const payloadLen = Math.ceil(jsonStr.length / FRAME_LEN) * FRAME_LEN
    const payload = encodeStringToBytes(jsonStr, payloadLen)
    await this.writeAppDefine(payload)

    // 2. 复位指定板载（0xEE）
    await device.sendRawApCommand({
      command: GCOME_AP_CMD.factoryReset,
      data: [index],
    })

    // 3. 更新板载顺序：从顺序中移除被删除的槽位
    //    取前 (maxProfiles - 1) 个为活跃顺序，剩余用未使用的槽位索引填充（避免重复）
    const filtered = profileOrder.filter((id) => id !== index)
    const newActiveCount = Math.max(1, maxProfiles - 1)
    const newActive = filtered.slice(0, newActiveCount)
    const usedSet = new Set(newActive)
    const newOrder = [...newActive]
    for (let i = 0; newOrder.length < this.maxConfigCount && i < this.maxConfigCount; i++) {
      if (!usedSet.has(i)) newOrder.push(i)
    }

    // 如果删除的是当前配置，切换到第一个有效配置
    const currentIndex = base[0]
    const targetIndex = currentIndex === index ? (newOrder[0] ?? 0) : currentIndex
    const switchPayload = [
      targetIndex,
      newActiveCount,
      newOrder[0],
      newOrder[1],
      newOrder[2],
      newOrder[3],
      0xaa,
      0xbb,
    ]
    await device.sendRawApCommand({
      command: GCOME_AP_CMD.switchOnboardProfile,
      data: switchPayload,
    })

    this.device.cache.invalidateByTag(`cfg:${index}`)
  }

  async initAllConfigs(): Promise<void> {
    const device = this.device as unknown as GcomeApKeyboardDevice
    // 复位所有板载
    await device.sendRawApCommand({
      command: GCOME_AP_CMD.factoryReset,
      data: [0xff],
    })
    // 清空 App Define 区（写全 0xFF = Flash 擦除态）：初始化后 SDK 独占该区，
    // 无需再为外部工具（网页版/官方驱动）的残留数据预留/避让地址。
    await this.clearAppDefine()
    this.device.cache.clearAll()
  }

  /**
   * 清空当前板载的 App Define 区（1024 字节全写 0xFF）。
   * 分帧写入，每帧 FRAME_LEN (56) 字节，地址从 0 递增。
   */
  private async clearAppDefine(): Promise<void> {
    await this.writeAppDefine(new Uint8Array(APP_DEFINE_TOTAL_SIZE).fill(0xff), 0)
  }

  /**
   * 获取当前板载配置快照。
   * 收录原则：仅收集可 get 且可 set 恢复的数据；无对应 set 的只读数据不写入 payload。
   */
  async getCurrentConfigSnapshot(): Promise<DeviceDataSnapshot> {
    const startTime = Date.now()
    const metadata = {
      deviceType: this.device.manifest.type,
      deviceModel: this.device.manifest.model,
      timestamp: startTime,
      sdkVersion: this.device.sdkVersion,
    }

    const payload: GcomeApConfigPayload = {
      configName: '',
      firmwareVersion: '',
      keyboard: {
        keysMap: [] as LayerKeysMap,
        keysMapFn: [] as LayerKeysMap,
        keysMapMac: [] as LayerKeysMap,
        keysMapMacFn: [] as LayerKeysMap,
        macroList: [] as Macro[],
        dksList: [] as DksSlotInfo[],
        mtList: [] as MtSlotInfo[],
        tglList: [] as TglSlotInfo[],
      },
      lighting: {
        lightingEffect: [] as KeyboardLightingEffect[],
        currentEffect: 0,
        brightness: 60,
        speed: 60,
        customStyle: [] as keysColor[],
        customRGBKeyMap: [] as { index: number; keyMapLighting: keyMapLighting }[],
      },
      sideLighting: {
        enabled: false,
        currentEffect: 0,
        customStyle: [] as keysColor[],
      },
      travel: {
        keyTravelList: [] as KeyTravelList,
      },
      performanceConfig: {
        cfg: {} as GcomeApPerfStatus,
      },
    }

    const keyboard = this.device.features.keyboardKeys
    const lighting = this.device.features.keyboardLighting
    const travel = this.device.features.keyTravel
    const firmware = this.device.features.firmware
    const keyboardPerfCfg = this.device.features.keyboardPerfCfg
    if (!keyboard || !lighting || !travel || !firmware || !keyboardPerfCfg) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED_VERSION, '当前使用SDK功能不完整')
    }

    const updateProgress = (progress: number) => {
      this.device.emit('config:Upload_current_config', progress)
    }
    updateProgress(0)

    // ========== 1：基础配置 (5%) ==========
    payload.configName = await this.getCurrentConfigName()
    try {
      payload.firmwareVersion = formatFirmwareVersions(await firmware.getFirmwareVersion())
    } catch {
      payload.firmwareVersion = ''
    }
    updateProgress(5)

    // ========== 2：灯效基础信息 (10%) -> 累计 15% ==========
    const [lightingEffects, currentEffect] = await Promise.all([
      lighting.getEffects(),
      lighting.getCurrentEffect(),
    ])
    const [brightness, speed] = await Promise.all([
      lighting.getBrightness().catch(() => 0),
      lighting.getSpeed().catch(() => 0),
    ])
    payload.lighting.lightingEffect = lightingEffects
    payload.lighting.currentEffect = currentEffect
    payload.lighting.brightness = brightness
    payload.lighting.speed = speed
    updateProgress(15)

    // ========== 3：灯效详情 (15%) -> 累计 30% ==========
    // GCOME 背光参数共享功能变量区；自定义逐键 RGB 在 LED 定义区（createCustomEffect 不支持）。
    const totalEffects = payload.lighting.lightingEffect.length
    for (let i = 0; i < totalEffects; i++) {
      const effect = payload.lighting.lightingEffect[i]
      const index = effect.id
      const effectMode = effect.kind

      if (effectMode === 'dynamic') {
        const { buffer: _buffer, ...rest } = await lighting.getConfig(
          LightingDomainType.Dynamic,
          index,
        )
        payload.lighting.customStyle.push(rest)
      } else if (effectMode === 'static') {
        const { buffer: _buffer, ...rest } = await lighting.getConfig(
          LightingDomainType.Static,
          index,
        )
        payload.lighting.customStyle.push(rest)
        if (effect.isCustom && lighting.getKeysColorStream) {
          const keyMapLightingData = [] as keyMapLighting
          for await (const keyRgb of lighting.getKeysColorStream(
            LightingDomainType.Static,
            index,
          )) {
            keyMapLightingData.push(keyRgb)
          }
          payload.lighting.customRGBKeyMap.push({
            index,
            keyMapLighting: keyMapLightingData,
          })
        }
      }

      const currentStepProgress = 15 + Math.round((15 * (i + 1)) / Math.max(totalEffects, 1))
      updateProgress(Math.min(currentStepProgress, 30))
    }
    updateProgress(30)

    // ========== 3.5：侧灯详情 (5%) -> 累计 35% ==========
    if (lighting.getSideLightDynamicEnable) {
      payload.sideLighting.enabled = await lighting.getSideLightDynamicEnable()
    }
    if (lighting.getSideEffect) {
      payload.sideLighting.currentEffect = await lighting.getSideEffect()
    }
    try {
      const sideEffects = await lighting.getEffects(LIGHTING_TARGET.Side)
      for (const effect of sideEffects) {
        try {
          const { buffer: _buffer, ...rest } = await lighting.getConfig(
            LightingDomainType.Side,
            effect.id,
          )
          payload.sideLighting.customStyle.push(rest)
        } catch {
          /* skip failed side effect */
        }
      }
    } catch {
      /* no side lighting */
    }
    updateProgress(35)

    // ========== 4：键盘映射（四层全量，GCOME 任意系统可编辑各层）(15%) -> 累计 50% ==========
    const BATCH_SIZE = 22
    const progressStart = 35
    const progressEnd = 50
    const progressRange = progressEnd - progressStart
    const layers: { isFn: boolean; platform: platform }[] = [
      { isFn: false, platform: 'win' },
      { isFn: true, platform: 'win' },
      { isFn: false, platform: 'mac' },
      { isFn: true, platform: 'mac' },
    ]

    let totalChunks = 0
    for (const layer of layers) {
      totalChunks += Math.ceil(keyboard.getLayerKeyCount(layer.isFn, layer.platform) / BATCH_SIZE)
    }
    let completedChunks = 0

    const readLayerKeysMapStream = async (
      isFnLayer: boolean,
      layerPlatform: platform,
    ): Promise<LayerKeysMap> => {
      const result: LayerKeysMap = []
      if (keyboard.getLayerKeysMapStream) {
        for await (const chunk of keyboard.getLayerKeysMapStream(
          isFnLayer,
          BATCH_SIZE,
          layerPlatform,
        )) {
          result.push(...chunk)
          completedChunks++
          const progress =
            progressStart + Math.round((progressRange * completedChunks) / Math.max(totalChunks, 1))
          updateProgress(Math.min(progress, progressEnd))
        }
      }
      return result
    }

    payload.keyboard.keysMap = await readLayerKeysMapStream(false, 'win')
    payload.keyboard.keysMapFn = await readLayerKeysMapStream(true, 'win')
    payload.keyboard.keysMapMac = await readLayerKeysMapStream(false, 'mac')
    payload.keyboard.keysMapMacFn = await readLayerKeysMapStream(true, 'mac')
    updateProgress(50)

    // ========== 5：宏数据 (10%) -> 累计 60% ==========
    if (keyboard.getMacroListStream) {
      for await (const macroChunk of keyboard.getMacroListStream()) {
        payload.keyboard.macroList.push(...macroChunk)
      }
    }
    const macroList = payload.keyboard.macroList.filter((macro) => macro.index !== undefined)
    const totalMacros = macroList.length
    for (let i = 0; i < totalMacros; i++) {
      const macro = macroList[i]
      const data = await keyboard.getMacroByIndex(macro.index!)
      Object.assign(macro, data)
      const currentStepProgress = 50 + Math.round((10 * (i + 1)) / Math.max(totalMacros, 1))
      updateProgress(Math.min(currentStepProgress, 60))
    }
    updateProgress(60)

    // ========== 5.5：高级改键 DKS/MT/TGL (15%) -> 累计 75% ==========
    if (keyboard.getDksList) {
      payload.keyboard.dksList = await keyboard.getDksList()
    }
    updateProgress(65)
    if (keyboard.getMtList) {
      payload.keyboard.mtList = await keyboard.getMtList()
    }
    updateProgress(70)
    if (keyboard.getTglList) {
      payload.keyboard.tglList = await keyboard.getTglList()
    }
    updateProgress(75)

    // ========== 6：行程 (20%) -> 累计 95%（芯片 utils 分帧回调，不改 KeyTravel Feature 合同） ==========
    const gcomeDevice = this.device as unknown as GcomeApKeyboardDevice
    const travelBlock = await readTravelBlock(
      gcomeDevice,
      undefined,
      mapFrameProgress(updateProgress, 75, 95),
    )
    payload.travel.keyTravelList = parseTravelInfo(travelBlock)
    updateProgress(95)

    // ========== 7：性能配置 (5%) -> 累计 100% ==========
    payload.performanceConfig.cfg = (await keyboardPerfCfg.getAllPerfCfg()) as GcomeApPerfStatus
    updateProgress(100)

    console.log(`GCOME_AP 快照读取完成，总耗时：${Date.now() - startTime}ms`)
    return { metadata, payload }
  }

  /**
   * 将快照写回当前板载配置。
   */
  async setCurrentConfigSnapshot(snapshot: DeviceDataSnapshot): Promise<void> {
    const startTime = Date.now()
    const { metadata, payload } = snapshot as DeviceDataSnapshot<GcomeApConfigPayload>

    const keyboard = this.device.features.keyboardKeys
    const lighting = this.device.features.keyboardLighting
    const travel = this.device.features.keyTravel
    const keyboardPerfCfg = this.device.features.keyboardPerfCfg
    if (!keyboard || !lighting || !travel || !keyboardPerfCfg) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED_VERSION, '当前使用SDK功能不完整')
    }
    if (metadata === undefined || metadata.sdkVersion === undefined) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED_VERSION, '当前使用SDK版本不匹配')
    }
    if (metadata.deviceModel !== this.device.manifest.model) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '设备匹配失败')
    }
    if (metadata.deviceType !== this.device.manifest.type) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '设备匹配失败2')
    }
    if (payload === undefined) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '数据异常')
    }

    const updateProgress = (progress: number) => {
      this.device.emit('config:Download_current_config', progress)
    }
    updateProgress(0)
    updateProgress(5)

    if (payload?.configName) {
      updateProgress(10)
    }

    // ========== 2：灯效详情 (5→30) ==========
    if (payload?.lighting) {
      const totalEffects = payload.lighting.lightingEffect?.length || 0
      if (totalEffects === 0) {
        updateProgress(30)
      } else {
        updateProgress(5)
        for (let i = 0; i < payload.lighting.lightingEffect.length; i++) {
          const effect = payload.lighting.lightingEffect[i]
          const index = effect.id
          const effectMode = effect.kind
          const style = payload.lighting.customStyle.find((item) => item.index === index)
          if (!style) {
            const currentProgress = 5 + Math.round((25 * (i + 1)) / totalEffects)
            updateProgress(Math.min(currentProgress, 30))
            continue
          }

          if (effectMode === 'dynamic') {
            await lighting.setConfig(LightingDomainType.Dynamic, index, {
              brightness: style.brightness ?? 60,
              speed: style.speed ?? 60,
              isColorful: style.isColorful,
              rgb: style.rgb,
            })
          }

          if (effectMode === 'static') {
            // GCOME 无 createCustomEffect：自定义逐键靠 setConfig + setKeysColor 恢复
            await lighting.setConfig(LightingDomainType.Static, index, {
              brightness: style.brightness ?? 60,
              speed: style.speed ?? 60,
              isColorful: style.isColorful,
              rgb: style.rgb,
            })
            if (effect.isCustom) {
              const customRGBKeyMap = payload.lighting.customRGBKeyMap.find(
                (item) => item.index === index,
              )
              if (customRGBKeyMap) {
                await lighting.setKeysColor(
                  LightingDomainType.Static,
                  customRGBKeyMap.keyMapLighting,
                  index,
                )
              }
            }
          }

          const currentProgress = 5 + Math.round((25 * (i + 1)) / totalEffects)
          updateProgress(Math.min(currentProgress, 30))
        }
      }

      await lighting.setProperty(
        LightingDomainType.Dynamic,
        payload.lighting.currentEffect,
        'brightness',
        payload.lighting.brightness ?? 60,
      )
      await lighting.setProperty(
        LightingDomainType.Dynamic,
        payload.lighting.currentEffect,
        'speed',
        payload.lighting.speed ?? 60,
      )
      await lighting.setCurrentEffect(payload.lighting.currentEffect ?? 0)
      updateProgress(32)
    } else {
      updateProgress(32)
    }

    // ========== 3：侧灯 (→35) ==========
    if (payload?.sideLighting) {
      if (lighting.setSideLightDynamicEnable) {
        await lighting.setSideLightDynamicEnable(payload.sideLighting.enabled)
      }
      for (const style of payload.sideLighting.customStyle) {
        await lighting.setConfig(LightingDomainType.Side, style.index!, {
          brightness: style.brightness,
          speed: style.speed,
          isColorful: style.isColorful,
          rgb: style.rgb,
        })
      }
      if (lighting.setSideEffect && payload.sideLighting.currentEffect !== undefined) {
        await lighting.setSideEffect(payload.sideLighting.currentEffect)
      }
    }
    updateProgress(35)

    // ========== 4：键盘映射四层 (35→50) ==========
    if (payload?.keyboard) {
      const writeLayer = async (
        keyMap: LayerKeysMap | undefined,
        isFn: boolean,
        layerPlatform: platform,
        progressFrom: number,
        progressTo: number,
      ) => {
        if (!keyMap || keyMap.length === 0) {
          updateProgress(progressTo)
          return
        }
        const total = keyMap.length
        for (let i = 0; i < total; i++) {
          await keyboard.setLayerKeysMap(keyMap[i], isFn, layerPlatform)
          const currentProgress =
            progressFrom + Math.round(((progressTo - progressFrom) * (i + 1)) / total)
          updateProgress(Math.min(currentProgress, progressTo))
        }
      }

      await writeLayer(payload.keyboard.keysMap, false, 'win', 35, 39)
      await writeLayer(payload.keyboard.keysMapFn, true, 'win', 39, 43)
      await writeLayer(payload.keyboard.keysMapMac, false, 'mac', 43, 47)
      await writeLayer(payload.keyboard.keysMapMacFn, true, 'mac', 47, 50)
    } else {
      updateProgress(50)
    }

    // ========== 5：宏 (50→60) ==========
    if (payload?.keyboard?.macroList) {
      const macroList = payload.keyboard.macroList.filter((m) => m.index !== undefined)
      const totalMacros = macroList.length
      if (totalMacros === 0) {
        updateProgress(60)
      } else {
        updateProgress(50)
        for (let i = 0; i < totalMacros; i++) {
          const macro = macroList[i]
          await keyboard.setMacroByIndex(macro, macro.index ?? 0)
          const currentProgress = 50 + Math.round((10 * (i + 1)) / totalMacros)
          updateProgress(Math.min(currentProgress, 60))
        }
      }
    } else {
      updateProgress(60)
    }

    // ========== 5.5：高级改键整表写入 (60→75) ==========
    const gcomeKeys = keyboard as GcomeApKeyboardKeys
    if (payload?.keyboard?.dksList?.length) {
      await gcomeKeys.setDksList(payload.keyboard.dksList)
    }
    updateProgress(65)
    if (payload?.keyboard?.mtList?.length) {
      await gcomeKeys.setMtList(payload.keyboard.mtList)
    }
    updateProgress(70)
    if (payload?.keyboard?.tglList?.length) {
      await gcomeKeys.setTglList(payload.keyboard.tglList)
    }
    updateProgress(75)

    // ========== 6：行程 (75→95，utils 分帧回调) ==========
    if (payload?.travel?.keyTravelList?.length) {
      await this.writeTravelSnapshot(
        payload.travel.keyTravelList,
        mapFrameProgress(updateProgress, 75, 95),
      )
    }
    updateProgress(95)

    // ========== 7：性能配置 (95→100) ==========
    if (payload?.performanceConfig?.cfg) {
      await keyboardPerfCfg.setAllPerfCfg(payload.performanceConfig.cfg)
    }
    updateProgress(100)

    this.device.cache.invalidateByTag(`cfg:${this.device.currentConfig.index}`)
    console.log(`GCOME_AP 快照应用完成，总耗时：${Date.now() - startTime}ms`)
  }

  /**
   * 快照还原专用行程写回：与 setKeyTravel 同语义（读-改-写），
   * 分帧进度仅走芯片 utils，不扩展 KeyTravelFeature 合同。
   */
  private async writeTravelSnapshot(
    keyTravelList: KeyTravelWriteList,
    onProgress?: InfoBlockProgress,
  ): Promise<void> {
    const device = this.device as unknown as GcomeApKeyboardDevice
    const mapHalf = (phase: 0 | 1): InfoBlockProgress | undefined => {
      if (!onProgress) return undefined
      return (done, total) => onProgress(phase * total + done, total * 2)
    }
    const block = await readTravelBlock(device, undefined, mapHalf(0))
    const items = parseTravelInfo(block)
    for (const info of keyTravelList) {
      if (info.family !== KeyTravelFamily.Gcome) {
        throw new DeviceError(
          DeviceErrorCode.INVALID_PARAM,
          'GCOME_AP setKeyTravel requires family: KeyTravelFamily.Gcome',
        )
      }
      if (info.index < 0 || info.index >= TRAVEL_KEY_COUNT) continue
      items[info.index] = {
        family: KeyTravelFamily.Gcome,
        index: info.index,
        keyTravel: { ...items[info.index].keyTravel, ...info.keyTravel },
      }
    }
    await writeTravelBlock(device, encodeTravelInfo(items), undefined, mapHalf(1))
  }
}
